import type { IConfigStore } from "./configstore";
import {
  AnalyticsIds,
  type AMP,
  type Campaign,
  type SourceTouch,
  type SyftIDs,
} from "./event_types";
import { getClickIdsFromParams } from "./ad_ids";
import { getAllCookies, getCookie } from "./cookies";
import { searchParams } from "./utils";

export { getAdCookies, getClickIdsFromParams } from "./ad_ids";

export function getCampaignFromParams(
  params: URLSearchParams
): Campaign | undefined {
  const results: Campaign = {};

  for (const key of params.keys()) {
    if (key.substring(0, 4) === "utm_") {
      let param = key.substring(4);
      if (param === "campaign") param = "name";
      (results as any)[param] = params.get(key);
    }
  }
  if (Object.keys(results).length === 0) return undefined;
  return results;
}

export function getSyftIds(params: URLSearchParams): SyftIDs | undefined {
  const ids: SyftIDs = {};
  if (params != null) {
    for (const key of params.keys()) {
      if (key === "sy_e") {
        ids.email = params.get(key)!;
      } else if (key === "sy_d") {
        ids.domain = params.get(key)!;
      }
    }
  }
  if (Object.keys(ids).length === 0) return undefined;
  return ids;
}

export function getAMP(): AMP | undefined {
  const ampId = getCookie("_ga");
  if (ampId != null) {
    return {
      id: ampId,
    };
  }
}

export function getPostHogId(): string | undefined {
  // Preferred path: ask the PostHog SDK directly when the site exposes it on
  // `window`. `get_distinct_id()` can throw if PostHog isn't initialized yet,
  // so guard it.
  try {
    const posthog = window.posthog;
    if (posthog != null && typeof posthog.get_distinct_id === "function") {
      const id = posthog.get_distinct_id();
      if (typeof id === "string" && id.length > 0) return id;
    }
  } catch {
    // fall through to storage lookup
  }

  // Fallback: posthog-js persists its state under a `ph_<projectApiKey>_posthog`
  // key in localStorage (and mirrors it to a cookie). This works even when the
  // page bundles posthog as a module and never assigns `window.posthog`.
  return getPostHogIdFromStorage();
}

function getPostHogIdFromStorage(): string | undefined {
  const isPostHogKey = (key: string): boolean =>
    key.startsWith("ph_") && key.endsWith("_posthog");

  const extractDistinctId = (
    raw: string | null | undefined
  ): string | undefined => {
    if (raw == null) return;
    // localStorage holds raw JSON; cookies may be URL-encoded.
    for (const candidate of [raw, safeDecodeURIComponent(raw)]) {
      if (candidate == null) continue;
      try {
        const id = (JSON.parse(candidate) as { distinct_id?: unknown })
          ?.distinct_id;
        if (typeof id === "string" && id.length > 0) return id;
      } catch {
        // not parseable, try next candidate
      }
    }
  };

  // 1) localStorage is the default (and most complete) posthog-js persistence.
  try {
    const store = window.localStorage;
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i);
      if (key != null && isPostHogKey(key)) {
        const id = extractDistinctId(store.getItem(key));
        if (id != null) return id;
      }
    }
  } catch {
    // localStorage can be unavailable (privacy mode, sandboxed iframe)
  }

  // 2) Cookie fallback (posthog also mirrors persistence into a cookie).
  try {
    const all = getAllCookies();
    for (const key of Object.keys(all)) {
      if (isPostHogKey(key)) {
        const id = extractDistinctId(all[key]);
        if (id != null) return id;
      }
    }
  } catch {
    // ignore
  }

  return undefined;
}

function safeDecodeURIComponent(value: string): string | undefined {
  try {
    return decodeURIComponent(value);
  } catch {
    return undefined;
  }
}

// function getSegmentId(): string | undefined {
//   if (typeof window.analytics == "undefined") return;
//   if (typeof window.analytics.ready != "function") return;
//   return window.analytics.user().id();
// }

export function getAnalyticsIds(): AnalyticsIds | undefined {
  const { search } = location;
  const params = searchParams(search);
  const ids: AnalyticsIds = {};

  const postHogId = getPostHogId();
  if (postHogId != null) {
    ids.postHogId = postHogId;
  }

  // const segmentId = getSegmentId();
  // if (segmentId != null) {
  //   ids.segementId = segmentId;
  // }

  if (params != null) {
    for (const key of params.keys()) {
      if (key === "_hsenc") {
        ids.hsenc = params.get(key)!;
      }
    }
  }
  // read cookies.
  const hubspotutk = getCookie("hubspotutk");
  if (hubspotutk != null) {
    ids.hubspotutk = hubspotutk;
  }

  if (Object.keys(ids).length === 0) return;
  return ids;
}

/**
 * Reads the traffic source (UTM campaign, ad click IDs, Syft IDs) from the
 * given URL search string, defaulting to the current page.
 */
export function getSourceTouch(search: string = location.search): SourceTouch {
  const params = searchParams(search);
  const campaign = getCampaignFromParams(params);
  const clickIds = getClickIdsFromParams(params);
  const syftIds = getSyftIds(params);
  return {
    campaign,
    clickIds,
    // Lets the backend rebuild time-stamped IDs such as Meta's `fbc`.
    clickTs: clickIds != null ? Date.now() : undefined,
    syftIds,
  };
}

function hasAttribution(touch: SourceTouch): boolean {
  return touch.campaign != null || touch.clickIds != null;
}

const SOURCE_EXPIRATION_DAYS = 1;
const SOURCE_TOUCH_KEY = "source_touch";

/**
 * Returns the session's source touch, updating it from `touch`.
 *
 * A touch with attribution (campaign or click IDs) replaces the previous one
 * as a whole, so a new campaign is never paired with an older click ID.
 * Without attribution, the previous touch is kept. Syft IDs identify the
 * visitor rather than the source, so they carry over independently.
 */
export function getSessionSourceTouch(
  configStore: IConfigStore,
  touch: SourceTouch = getSourceTouch()
): SourceTouch | undefined {
  const existingSourceTouch = configStore.get(SOURCE_TOUCH_KEY) as
    | SourceTouch
    | undefined;
  const sourceTouch: SourceTouch =
    hasAttribution(touch) || existingSourceTouch == null
      ? {
          campaign: touch.campaign,
          clickIds: touch.clickIds,
          clickTs: touch.clickTs,
        }
      : {
          campaign: existingSourceTouch.campaign,
          clickIds: existingSourceTouch.clickIds,
          clickTs: existingSourceTouch.clickTs,
        };
  sourceTouch.syftIds = touch.syftIds ?? existingSourceTouch?.syftIds;
  configStore.setWithExpiration(
    SOURCE_TOUCH_KEY,
    sourceTouch,
    SOURCE_EXPIRATION_DAYS
  );
  return sourceTouch;
}

const INITIAL_SOURCE_EXPIRATION_DAYS = 30;
const INITIAL_SOURCE_TOUCH_KEY = "initial_source_touch";
export function getInitialSourceTouch(
  configStore: IConfigStore,
  touch: SourceTouch = getSourceTouch()
): SourceTouch | undefined {
  const sourceTouch = configStore.get(INITIAL_SOURCE_TOUCH_KEY) as
    | SourceTouch
    | undefined;
  if (sourceTouch != null) {
    return sourceTouch;
  }
  configStore.setWithExpiration(
    INITIAL_SOURCE_TOUCH_KEY,
    touch,
    INITIAL_SOURCE_EXPIRATION_DAYS
  );
  return touch;
}
